import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Zap, Check } from "lucide-react";

/**
 * UpgradeModal Component
 * Shows when free users try to access premium features
 */
const UpgradeModal = ({ isOpen, onClose, feature, limit }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    onClose();
    navigate("/subscription");
  };

  const getFeatureMessage = () => {
    switch (feature) {
      case "analyzer_limit":
        return {
          title: "Analyzer Limit Reached",
          description: `You've reached the limit of ${limit} CRO analysis on the free plan.`,
          benefits: [
            "Unlimited CRO analyses",
            "Competitor analysis",
            "Export to PDF/CSV",
            "Advanced recommendations",
            "Priority support",
          ],
        };
      case "route_access":
        return {
          title: "Premium Feature",
          description:
            "This feature is only available on Standard and Pro plans.",
          benefits: [
            "AI Marketing strategies",
            "Goals tracking",
            "Data source connections",
            "Advanced analytics",
            "Priority support",
          ],
        };
      default:
        return {
          title: "Upgrade Required",
          description: "This feature requires a paid plan.",
          benefits: [
            "Unlimited CRO analyses",
            "All premium features",
            "Priority support",
          ],
        };
    }
  };

  const message = getFeatureMessage();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-4 md:p-6 relative max-h-[90vh] overflow-y-auto">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 md:top-4 md:right-4 text-gray-400 hover:text-gray-600 touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-3 md:mb-4">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-primary-500 to-blue-600 rounded-full flex items-center justify-center">
              <Zap className="w-7 h-7 md:w-8 md:h-8 text-white" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-2">
            {message.title}
          </h2>

          {/* Description */}
          <p className="text-sm md:text-base text-gray-600 text-center mb-4 md:mb-6">
            {message.description}
          </p>

          {/* Benefits */}
          <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
            <p className="text-sm font-semibold text-gray-900 mb-2 md:mb-3">
              Upgrade to unlock:
            </p>
            <ul className="space-y-2">
              {message.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start">
                  <Check className="w-4 h-4 md:w-5 md:h-5 text-primary-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-xs md:text-sm text-gray-700">
                    {benefit}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Money-back guarantee badge */}
          <div className="text-center mb-4">
            <span className="inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <Check className="w-3 h-3 mr-1" />
              14-Day Money-Back Guarantee
            </span>
          </div>

          {/* Actions */}
          <div className="flex gap-2 md:gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-3 md:px-4 py-2.5 md:py-2 border border-gray-300 rounded-lg text-sm md:text-base text-gray-700 font-medium hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation min-h-[44px]"
            >
              Cancel
            </button>
            <button
              onClick={handleUpgrade}
              className="flex-1 px-3 md:px-4 py-2.5 md:py-2 bg-gradient-to-r from-primary-600 to-blue-600 text-white rounded-lg text-sm md:text-base font-medium hover:from-primary-700 hover:to-blue-700 active:from-primary-800 active:to-blue-800 transition-colors shadow-md touch-manipulation min-h-[44px]"
            >
              View Plans
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default UpgradeModal;
